import { Component, Input, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import {
  CadModelMaterialType,
  CuttingCadModel,
  CuttingCadModelMaterialLink,
  LegacyDialogsService,
  MaterialType,
} from 'cloud-shared-lib';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { OrderModelsService } from '../order-models.service';

@Component({
  selector: 'cutting-cadmaterials-link-dialog',
  templateUrl: './cadmaterials-link-dialog.component.html',
  styleUrls: ['./cadmaterials-link-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CadMaterialsLinkDialogComponent implements OnDestroy {
  matGroupingCheckbox = false;
  unsubscribe$: Subject<void> = new Subject();

  cadModel: CuttingCadModel[] = [];
  @Input() set cadModels(val: CuttingCadModel[]) {
    this.cadModel = _.cloneDeep(val);
    if (!this.checkLinkedMaterial(this.cadModel[0])) this.findMaterialsInMatbase(this.cadModel);
  }
  get cadModels(): CuttingCadModel[] {
    return this.cadModel;
  }

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<CadMaterialsLinkDialogComponent>,
    private dialogsService: LegacyDialogsService,
    private orderModelsService: OrderModelsService,
    private snackBar: MatSnackBar
  ) {}

  findMaterialsInMatbase(models: CuttingCadModel[]): void {
    const materials = _.flatMap(models, m => m.parsedMaterials);
    const materialsForFind = _.uniqBy(materials, m => m.originalMaterialFullName)
      .filter(m => m.materialType !== CadModelMaterialType.Side)
      .map(m => m.originalMaterialFullName);

    const mapMaterialType = (type: MaterialType): CadModelMaterialType => {
      const map = {
        [MaterialType.Butt]: CadModelMaterialType.Side,
        [MaterialType.Plate]: CadModelMaterialType.Plate,
        [MaterialType.Linear]: CadModelMaterialType.Linear,
      };

      return map[type];
    };

    this.orderModelsService
      .findMaterialInMatbase(materialsForFind)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(ls => {
        materials.forEach(m => {
          if (ls[m.originalMaterialFullName]) {
            const linked = ls[m.originalMaterialFullName];

            m.linkedMaterialId = linked.inMaterialBaseId;
            m.linkedGroupMaterialId = linked.inMaterialBaseGroupId;
            m.linkedMaterialName = linked.name;
            m.linkedMaterialArticle = linked.article;
            m.linkedMaterialThickness = linked.thickness;
            m.linkedMaterialType = mapMaterialType(linked.type);
            m.height = linked.height;
            m.width = linked.width;
          }
        });
      });

    const buttsForFind = _.uniqBy(materials, m => m.originalMaterialFullName)
      .filter(m => m.materialType === CadModelMaterialType.Side)
      .map(m => m.originalMaterialFullName);

    this.orderModelsService
      .findButtMaterialInMatbase(buttsForFind)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(ls => {
        materials.forEach(m => {
          if (ls[m.originalMaterialFullName]) {
            const linked = ls[m.originalMaterialFullName];

            m.linkedMaterialId = linked.id;
            m.linkedGroupMaterialId = linked.groupId;
            m.linkedMaterialName = linked.name;
            m.linkedMaterialArticle = linked.article;
            m.linkedMaterialThickness = linked.thickness;
            m.linkedMaterialType = CadModelMaterialType.Side;
            m.linkedButtSign = linked.sign;
            m.linkedButtAllowance = linked.allowance;
            m.linkedButtOverhung = linked.overhang;
            m.linkedButtWidth = linked.width;
          }
        });
      });
  }

  public saveAndClose(): void {
    for (const model of this.cadModels) {
      if (
        model.parsedMaterials.some(
          sb => sb.materialType === CadModelMaterialType.Side && _.isEmpty(sb.linkedMaterialName)
        )
      ) {
        this.snackBar.open($localize`Назначьте соответствия облицовочному материалу`);
        return;
      }

      if (
        model.parsedMaterials.some(
          sb => sb.materialType === CadModelMaterialType.Slot && _.isEmpty(sb.linkedMaterialName)
        )
      ) {
        this.snackBar.open($localize`Назначьте соответствия пазам`);
        return;
      }

      const hasLinkedMaterials = model.parsedMaterials.some(
        fl =>
          !_.isEmpty(fl.linkedMaterialName) &&
          (fl.materialType === CadModelMaterialType.Plate || fl.materialType === CadModelMaterialType.Linear)
      );

      if (!hasLinkedMaterials) {
        this.snackBar.open($localize`Выберите материалы для раскроя`);
        return;
      }
    }

    const hasUnmatchedMaterials = this.cadModels.some(model => {
      model.parsedMaterials.some(
        fl =>
          _.isEmpty(fl.linkedMaterialName) &&
          (fl.materialType === CadModelMaterialType.Plate || fl.materialType === CadModelMaterialType.Linear)
      );
    });

    if (hasUnmatchedMaterials) {
      this.dialogsService
        .confirm(
          $localize`При сохранении модели, несопоставленные материалы будут удалены и раскраиваться не будут. Продолжить?`
        )
        .pipe(
          filter(res => res),
          tap(() => {
            this.dialogRef.close(this.cadModels);
            return;
          }),
          takeUntil(this.unsubscribe$)
        )
        .subscribe();
    } else {
      this.dialogRef.close(this.cadModels);
    }
  }

  getCommonMaterialLinks(models: CuttingCadModel[]): CuttingCadModelMaterialLink[] {
    return _.flatMap(models, m => m.parsedMaterials);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }

  private checkLinkedMaterial(models: CuttingCadModel): boolean {
    return models.parsedMaterials.some(result => result.linkedMaterialId);
  }
}
